import React, { useState, useMemo, useRef } from 'react';
import PageToolbar from '../util/PageToolbar';
import axios from 'axios';
import moment from 'moment';
import { t } from 'i18next';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyCheckDollar } from '@fortawesome/free-solid-svg-icons';
import DataTable from '../util/DataTable';
import { ColDef, BulkEditField } from '../util/DataTable';
import UserAvatar from '../util/UserAvatar';
import SearchFilter, { applySearchAndFilters, ActiveFilter } from '../util/SearchFilter';

interface Props {
  rows: any[];
  access: any;
  staticDateCompany: any;
  baseUrl?: string;
  onAdd: () => void;
  onEdit: (row: any) => void;
  refresh: () => void;
  panelToggle?: React.ReactNode;
}

const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  pending:   { color: '#f59e0b', bg: 'rgba(245,158,11,.12)' },
  rejected:  { color: '#ef4444', bg: 'rgba(239,68,68,.12)' },
  fulfilled: { color: '#3b82f6', bg: 'rgba(59,130,246,.12)' },
  paid:      { color: '#22c55e', bg: 'rgba(34,197,94,.12)' },
};

const STATUS_MENU_ITEMS = [
  { id: 1, title: 'pending' },
  { id: 2, title: 'rejected' },
  { id: 3, title: 'approved' },
];

export default function FinanceCarExpensesTable({
  rows, access, staticDateCompany, baseUrl = '',
  onAdd, onEdit, refresh, panelToggle,
}: Props) {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<number | null>(null);

  const canManage = access.car_exp_add === true || access.car_exp_add === 1 ||
    access.expenses_admin === true || access.expenses_admin === 1 ||
    access.expenses_manager === true || access.expenses_manager === 1 ||
    access.expenses_get === 3;

  const userOptions = useMemo(() => {
    const seen = new Set<string>();
    return (rows as any[])
      .reduce((acc: { value: string; label: string }[], r) => {
        if (r.user) {
          const name = `${r.user.first_name} ${r.user.last_name}`;
          if (!seen.has(name)) { seen.add(name); acc.push({ value: name, label: name }); }
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const carOptions = useMemo(() => {
    const seen = new Set<string>();
    return (rows as any[]).reduce((acc: { value: string; label: string }[], r) => {
      if (r.cars) {
        const name = `${r.cars.brand || ''} ${r.cars.model || ''}`.trim();
        if (name && !seen.has(name)) { seen.add(name); acc.push({ value: name, label: name }); }
      }
      return acc;
    }, []).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const categoryOptions = useMemo(() => {
    const seen = new Set<string>();
    return (rows as any[]).reduce((acc: { value: string; label: string }[], r) => {
      if (r.categoryTree && !seen.has(r.categoryTree)) { seen.add(r.categoryTree); acc.push({ value: r.categoryTree, label: r.categoryTree }); }
      return acc;
    }, []).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const handleSetStatus = async (id: number, rawStatus: string) => {
    const status = rawStatus === 'approved' ? 'fulfilled' : rawStatus;
    try {
      await axios.put('cars-expenses/update/status', { status, id: [id] });
      refresh();
    } catch {}
  };

  const handleMenuSelect = async (rawTitle: string) => {
    const id = menuRowId;
    setMenuAnchor(null);
    if (id != null) await handleSetStatus(id, rawTitle);
  };

  const handleApprove = (id: number) => handleSetStatus(id, 'fulfilled');
  const handleReject = (id: number) => handleSetStatus(id, 'rejected');

  const columns = useMemo((): ColDef[] => [
    {
      key: 'date', label: t('Date'), sortable: true,
      sortExtract: (r) => r.date ?? '',
      filterType: 'daterange',
      filterExtract: (r) => r.date ?? '',
      render: (r) => r.date ? moment(r.date).format(staticDateCompany) : '—',
    },
    {
      key: 'user', label: t('NameCreator'),
      filterType: 'select',
      filterOptions: userOptions,
      filterExtract: (r) => r.user ? `${r.user.first_name} ${r.user.last_name}` : '',
      render: (r) => r.user ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <UserAvatar user={r.user} baseUrl={baseUrl} size={28} />
          <span>{r.user.first_name} {r.user.last_name}</span>
        </div>
      ) : '—',
    },
    {
      key: 'categoryTree', label: t('Category'),
      filterType: 'select',
      filterOptions: categoryOptions,
      filterExtract: (r) => r.categoryTree || '',
      render: (r) => r.categoryTree || '—',
    },
    {
      key: 'supervisor', label: t('Supervisor'),
      render: (r) => r.supervisor
        ? `${r.supervisor.first_name} ${r.supervisor.last_name}`
        : '—',
    },
    {
      key: 'cars', label: t('Car'),
      filterType: 'select',
      filterOptions: carOptions,
      filterExtract: (r) => r.cars ? `${r.cars.brand || ''} ${r.cars.model || ''}`.trim() : '',
      render: (r) => r.cars ? `${r.cars.brand || ''} ${r.cars.model || ''}`.trim() || '—' : '—',
    },
    {
      key: 'destination', label: t('Destination'),
      render: (r) => r.destination || '—',
    },
    {
      key: 'distance', label: t('Distance'),
      render: (r) => r.distance || '—',
    },
    {
      key: 'purpose', label: t('Purpose'),
      render: (r) => r.purpose || '—',
    },
    {
      key: 'cost', label: t('CostCar'), sortable: true, align: 'right',
      sortExtract: (r) => Number(r.cost) || 0,
      render: (r) => r.cost != null ? Number(r.cost).toFixed(2) : '—',
    },
    {
      key: 'status', label: t('StatusExpense'), sortable: false,
      filterType: 'select',
      filterExtract: (r) => r.status ?? '',
      filterOptions: [
        { value: 'pending',   label: 'Pending' },
        { value: 'rejected',  label: 'Rejected' },
        { value: 'fulfilled', label: 'Approved' },
        { value: 'paid',      label: 'Paid' },
      ],
      render: (r) => {
        const sc = STATUS_COLORS[r.status];
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Tooltip title={r.status === 'rejected' && r.status_comment ? `${t('RejectionReason')}: ${r.status_comment}` : ''}>
              <Chip
                label={r.status === 'fulfilled' ? 'approved' : (r.status || '')}
                onClick={canManage ? (e) => { setMenuRowId(r.id); setMenuAnchor(e.currentTarget); } : undefined}
                variant="outlined"
                sx={{
                  height: 26, fontSize: '0.72rem',
                  borderRadius: '20px',
                  color: sc?.color ?? 'var(--text-color)',
                  borderColor: sc?.color ?? 'var(--border-input)',
                  background: sc?.bg ?? 'transparent',
                  cursor: canManage ? 'pointer' : 'default',
                  '& .MuiChip-label': { padding: '0 9px' },
                }}
              />
            </Tooltip>
            {r.status === 'pending' && canManage && (
              <>
                <Tooltip title={t('Approve') as string}>
                  <button
                    type="button"
                    className="fx-ar-btn ok"
                    onClick={() => handleApprove(r.id)}
                  >✓</button>
                </Tooltip>
                <Tooltip title={t('Reject') as string}>
                  <button
                    type="button"
                    className="fx-ar-btn no"
                    onClick={() => handleReject(r.id)}
                  >✗</button>
                </Tooltip>
              </>
            )}
          </div>
        );
      },
    },
    {
      key: '_attach', label: '📎', sortable: false,
      render: () => <span style={{ color: 'var(--text-color-calendar-week)', fontSize: 11 }}>—</span>,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [staticDateCompany, canManage, userOptions, carOptions, categoryOptions]);

  const shownRows = useMemo(
    () => applySearchAndFilters(rows, search, filters, columns, (r: any) => [
      r.user?.first_name, r.user?.last_name,
      r.categoryTree, r.supervisor?.first_name, r.supervisor?.last_name,
      r.cars?.brand, r.cars?.model, r.destination, r.purpose,
    ].filter(Boolean).join(' ')),
    [rows, search, filters, columns],
  );

  const bulkEditFields: BulkEditField[] | undefined = canManage ? [
    {
      key: 'status', label: t('StatusExpense'), type: 'select',
      options: [
        { value: 'pending',   label: 'Pending' },
        { value: 'rejected',  label: 'Rejected' },
        { value: 'fulfilled', label: 'Approved' },
        { value: 'paid',      label: 'Paid' },
      ],
    },
  ] : undefined;

  const handleBulkEdit = async (keys: (string | number)[], field: string, value: any) => {
    try {
      await axios.put('cars-expenses/update/status', { status: value, id: keys });
      refresh();
    } catch {}
  };

  const handleBulkDelete = canManage
    ? async (keys: (string | number)[]) => {
        await Promise.all((keys as number[]).map((id) => axios.delete(`cars-expenses/${id}`)));
        refresh();
      }
    : undefined;

  const toolbarSlot = useRef<HTMLDivElement>(null);

  return (
    <>
      <PageToolbar>
        <SearchFilter grow columns={columns} search={search} onSearch={setSearch} filters={filters} onFilters={setFilters} />
        {canManage && (
          <button type="button" onClick={onAdd} className="btn-primary">
            <AddIcon sx={{ fontSize: 15 }} />
            <span>{t('AddCarExpenses')}</span>
          </button>
        )}
        {panelToggle && <div className="divider-v" />}
        {panelToggle}
        <div style={{ flex: 1 }} />
        <div ref={toolbarSlot} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)' }} />
      </PageToolbar>
      <div style={{ flex: 1, minHeight: 0 }}>
        <DataTable
          rows={shownRows}
          getKey={(r: any) => r.id}
          columns={columns}
          filterable={false}
          onEdit={canManage ? onEdit : undefined}
          canEdit={(r: any) => r.status !== 'paid' && r.status !== 'fulfilled'}
          onDeleteMany={handleBulkDelete}
          bulkEditFields={bulkEditFields}
          onBulkEdit={handleBulkEdit}
          defaultPageSize={20}
          storageKey="finance-car-expenses-cols"
          footerIcon={<FontAwesomeIcon icon={faMoneyCheckDollar} className="text-[16px] opacity-30 text-[var(--text-color)]" />}
          toolbarRef={toolbarSlot}
        />
      </div>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        MenuListProps={{ dense: true, sx: { py: 0.5 } }}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 132 } } }}
      >
        {STATUS_MENU_ITEMS.map((s) => (
          <MenuItem
            key={s.id}
            onClick={() => handleMenuSelect(s.title)}
            sx={{ fontSize: 12.5, fontWeight: 600, minHeight: 30, py: 0.5, px: 1.5, textTransform: 'capitalize', gap: 1 }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: STATUS_COLORS[s.title === 'approved' ? 'fulfilled' : s.title]?.color ?? 'var(--text-color)' }} />
            {s.title}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
